// Baza na urządzeniu (IndexedDB). Dziennik zdarzeń tylko do dopisywania + pliki (zdjęcia, plany) + ustawienia.
// Migracje: każda nowa wersja bazy dopisuje krok w MIGRACJE; stare dane zostają (test: stara baza → nowa, 0 różnic).
import { openDB } from 'idb';

export const NAZWA_BAZY = 'aplikacja-budowy';
export const WERSJA_BAZY = 2;

const MIGRACJE = {
  1(db) {
    const z = db.createObjectStore('zdarzenia', { keyPath: 'id' });
    z.createIndex('budowa', 'budowaId');
    z.createIndex('wyslane', 'wyslane');
    db.createObjectStore('pliki', { keyPath: 'hash' });
    db.createObjectStore('ustawienia');
  },
  // 2 (14.10.2026): indeks plików niewysłanych na serwer, żeby synchronizacja nie czytała wszystkich zdjęć i planów
  2(db, tx) { tx.objectStore('pliki').createIndex('wyslany', 'wyslany'); },
};

let _db;
export async function baza() {
  if (_db) return _db;
  _db = await openDB(NAZWA_BAZY, WERSJA_BAZY, {
    upgrade(db, stara, _nowa, tx) { for (let v = stara + 1; v <= WERSJA_BAZY; v++) MIGRACJE[v](db, tx); },
    blocked() { console.warn('Baza zablokowana przez starszą kartę aplikacji'); },
    // nowsza wersja aplikacji (w innej karcie) chce podnieść bazę: zwalniamy ją i wczytujemy nową wersję, zamiast blokować aktualizację
    blocking() { _db?.close(); _db = null; if (typeof location !== 'undefined') location.reload(); },
  });
  return _db;
}

// pełna pamięć: przeglądarka odmawia zapisu (QuotaExceededError, bywa opakowany w błąd transakcji). Zamieniamy go na zrozumiały
// komunikat i NIE udajemy, że zapis się udał (zdarzenie nie trafia do widoku, użytkownik wie, że ma powtórzyć).
export const KOMUNIKAT_PELNEJ_PAMIECI = 'Brak miejsca w pamięci telefonu: ta zmiana NIE została zapisana. Zwolnij miejsce w telefonie (np. stare filmy lub zdjęcia w galerii) i zapisz jeszcze raz.';
export function pelnaPamiec(e, g = 0) {
  if (!e || g > 3) return false;
  return e.name === 'QuotaExceededError' || /quota/i.test(e.message || '') || pelnaPamiec(e.cause, g + 1) || pelnaPamiec(e.inner, g + 1) || pelnaPamiec(e.target?.error, g + 1);
}
const zapisLubBlad = async (f) => {
  try { return await f(); } catch (e) {
    if (!pelnaPamiec(e)) throw e;
    const b = new Error(KOMUNIKAT_PELNEJ_PAMIECI, { cause: e }); b.pelnaPamiec = true; throw b;
  }
};

/** Dopisuje zdarzenie. Istniejącego nie nadpisuje (dziennik tylko do dopisywania). */
export function dopisz(z) {
  return zapisLubBlad(async () => {
    const db = await baza();
    const tx = db.transaction('zdarzenia', 'readwrite');
    if (await tx.store.get(z.id)) { await tx.done; return false; }
    await tx.store.add({ ...z, wyslane: 0 });
    await tx.done;
    return true;
  });
}

/** Dopisuje wiele zdarzeń w jednej transakcji (wszystkie albo żadne). Istniejących nie nadpisuje. Zwraca liczbę nowych. */
export function dopiszWiele(lista) {
  return zapisLubBlad(async () => {
    // żądania wysłane naraz, bez czekania na każde z osobna (czekanie po kolei: 668 zdarzeń ok. 0,7 s samego oczekiwania)
    const db = await baza(); const tx = db.transaction('zdarzenia', 'readwrite');
    const jest = await Promise.all(lista.map((z) => tx.store.getKey(z.id)));
    const nowe = lista.filter((z, i) => jest[i] === undefined);
    await Promise.all([...nowe.map((z) => tx.store.add({ ...z, wyslane: 0 })), tx.done]); return nowe.length;
  });
}

export async function wszystkie() { return (await baza()).getAll('zdarzenia'); }
export async function niewyslane() { return (await baza()).getAllFromIndex('zdarzenia', 'wyslane', 0); }
export async function oznaczWyslane(ids, czasSerwera) {
  const db = await baza(); const tx = db.transaction('zdarzenia', 'readwrite');
  for (const id of ids) { const z = await tx.store.get(id); if (z) await tx.store.put({ ...z, wyslane: 1, czasSerwera: czasSerwera || z.czasSerwera }); }
  await tx.done;
}

/** Zdarzenie pobrane z serwera: już wysłane, więc nie wraca do kolejki. Istniejącego nie nadpisuje. */
export async function dopiszZSerwera(z) {
  const db = await baza(); const tx = db.transaction('zdarzenia', 'readwrite');
  const jest = await tx.store.get(z.id);
  if (jest) { if (!jest.czasSerwera && z.czasSerwera) await tx.store.put({ ...jest, czasSerwera: z.czasSerwera, wyslane: 1 }); await tx.done; return false; }
  await tx.store.add({ ...z, wyslane: 1 }); await tx.done; return true;
}
/** Zdarzenia odrzucone przez serwer (brak uprawnień) wychodzą z kolejki z oznaczeniem -1; zostają w dzienniku do wyjaśnienia. */
export async function oznaczOdrzucone(ids) {
  const db = await baza(); const tx = db.transaction('zdarzenia', 'readwrite');
  for (const id of ids) { const z = await tx.store.get(id); if (z) await tx.store.put({ ...z, wyslane: -1 }); }
  await tx.done;
}

/** Odtworzenie z kopii budowy (src/dokumenty/kopia.js): dopisuje brakujące zdarzenia i pliki, niczego nie nadpisuje.
 *  Zdarzenie, które było już na serwerze (ma czasSerwera), nie wraca do kolejki wysyłki. Zwraca { zdarzen, plikow } nowych. */
export function wgrajKopie(zdarzenia, pliki) { return zapisLubBlad(() => wgrajKopie0(zdarzenia, pliki)); }
async function wgrajKopie0(zdarzenia, pliki) {
  const db = await baza(); const tx = db.transaction(['zdarzenia', 'pliki'], 'readwrite'); const Zs = tx.objectStore('zdarzenia'), Ps = tx.objectStore('pliki');
  let zdarzen = 0, plikow = 0;
  for (const z of zdarzenia) if (!(await Zs.get(z.id))) { await Zs.add({ ...z, wyslane: z.czasSerwera ? 1 : 0 }); zdarzen++; }
  for (const { hash, dane, meta } of pliki) if (!(await Ps.get(hash))) {
    await Ps.put({ hash, dane: dane.buffer.slice(dane.byteOffset, dane.byteOffset + dane.byteLength), typ: meta.typ || '', rozmiar: dane.byteLength, nazwa: meta.nazwa || undefined, rodzaj: meta.rodzaj || undefined, wyslany: 0 }); plikow++; }
  await tx.done; return { zdarzen, plikow };
}

export async function ustawienie(klucz, wartosc) {
  const db = await baza();
  if (wartosc === undefined) return db.get('ustawienia', klucz);
  return db.put('ustawienia', wartosc, klucz);
}

/** Plik (zdjęcie, plan) zapisany pod swoim skrótem SHA-256: ten sam plik nie zapisze się dwa razy, a skrót jest dowodem, że plik się nie zmienił. */
// Plik zapisujemy jako ArrayBuffer, nie Blob: część silników WebKit nie umie zapisać Blob w IndexedDB
// („Error preparing Blob/File data to be stored”, wykryte 12.10 w teście WebKit), a ArrayBuffer działa wszędzie.
export async function zapiszPlik(blob, meta = {}) {
  // zdjęcie z aparatu: zmniejszone przed zapisem (src/zdjecia.js); oryginał zostaje tylko, gdy zmniejszenie się nie uda
  if (meta.rodzaj === 'zdjecie' && typeof document !== 'undefined') {
    const { zmniejsz } = await import('./zdjecia.js'); const m = await zmniejsz(blob);
    if (m !== blob) { meta = { ...meta, nazwa: m.name, rozmiarOryginalu: blob.size }; blob = m; }
  }
  const dane = await blob.arrayBuffer();
  const hash = await sha256(dane);
  const db = await baza();
  await zapisLubBlad(async () => { if (!(await db.get('pliki', hash))) await db.put('pliki', { hash, dane, typ: blob.type, rozmiar: blob.size, ...meta, wyslany: 0 }); });
  if (meta.rodzaj === 'zdjecie' && typeof window !== 'undefined') window.dispatchEvent(new Event('pamiec')); // pasek stanu przelicza zajęte miejsce
  return hash;
}
/** Pliki do wysłania na serwer (skróty) i oznaczenie wysłanego; plik pobrany z serwera zapisujemy jako już wysłany. */
export async function plikiNiewyslane() { return (await baza()).getAllKeysFromIndex('pliki', 'wyslany', 0); }
export async function oznaczPlikWyslany(hash) {
  const db = await baza(); const tx = db.transaction('pliki', 'readwrite'); const f = await tx.store.get(hash);
  if (f && !f.wyslany) await tx.store.put({ ...f, wyslany: 1 }); await tx.done;
}
export async function maPlik(hash) { return (await (await baza()).getKey('pliki', hash)) !== undefined; }
export function zapiszPlikZSerwera(hash, dane, typ) {
  return zapisLubBlad(async () => { const db = await baza(); if (!(await db.getKey('pliki', hash))) await db.put('pliki', { hash, dane, typ: typ || '', rozmiar: dane.byteLength, wyslany: 1 }); });
}
export async function plik(hash) {
  const p = await (await baza()).get('pliki', hash);
  if (!p) return null;
  return { ...p, blob: p.blob || new Blob([p.dane], { type: p.typ || 'application/octet-stream' }) };
}

export async function sha256(dane) {
  const buf = await crypto.subtle.digest('SHA-256', dane instanceof ArrayBuffer ? dane : await dane.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Prośba o trwałą pamięć (żeby przeglądarka nie wyczyściła danych). Zwraca stan do pokazania użytkownikowi. */
export async function trwalaPamiec() {
  try {
    if (!navigator.storage?.persist) return { trwala: false, wspierane: false };
    const juz = await navigator.storage.persisted();
    const trwala = juz || (await navigator.storage.persist());
    const est = await navigator.storage.estimate?.();
    return { trwala, wspierane: true, zajete: est?.usage ?? null, limit: est?.quota ?? null };
  } catch { return { trwala: false, wspierane: false }; }
}

/** Zajęte miejsce i limit przeglądarki dla aplikacji (bajty); null, gdy przeglądarka tego nie podaje. */
export async function zajetaPamiec() {
  try { const e = await navigator.storage?.estimate?.(); return e ? { zajete: e.usage ?? null, limit: e.quota ?? null } : null; } catch { return null; }
}
